import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonV2 } from '@asigna/kit';
import cx from 'classnames';
import { OwnerAvatar, SafeAvatar } from 'components';
import { CopyAddress } from 'components/CopyAddress/CopyAddress';
import { ShortText } from 'components/ShortText/ShortText';
import { isActivatedPro, useSidebarState } from 'hooks';
import VerificationIcon from 'images/verifySafeAddress.svg';
import { useArchivedSafes, useSafes, useSelectedSubAccounts, useSubAccountForSafe } from 'store';
import { IClaimMultiSignWallet, IMultiSignWallet } from 'types/api/ApiResponseTypes';
import { formatSafeDate, shortenAddress } from 'utils/helper';

import { useTreasuryStyles } from '../useTreasuryStyles';

import { SubAccountsDropdown } from './SubAccountsDropdown';

interface MultisigSafeProps {
  safe: IClaimMultiSignWallet;
  index?: number;
}

// TODO_R: [RP] SafeCard
export const MultisigSafe: React.FC<MultisigSafeProps> = ({ safe, index = -1 }) => {
  const classes = useTreasuryStyles();
  const { setCurrentSafeId, setCurrentParentSafeId } = useSidebarState();
  const history = useHistory();
  const { safes } = useSafes();
  const { archivedSafes } = useArchivedSafes();

  const isPro = isActivatedPro(safe as unknown as IMultiSignWallet, safes);

  const { selectSubAccount } = useSelectedSubAccounts();

  const subaccounts = useMemo(() => {
    return [...safes, ...archivedSafes].filter((item) => item.parrentId === safe._id);
  }, [safe, archivedSafes]);

  const currentSubAccount = useSubAccountForSafe(safe._id);

  const subaccountSafe = useMemo(() => {
    return [...safes, ...archivedSafes].find((safe) => safe._id === currentSubAccount);
  }, [safes, archivedSafes, currentSubAccount]);

  return (
    <button
      className="flex flex-col items-center justify-between gap-16 px-16 sm:px-24 py-16 sm:py-20 rounded-[12px] bg-black-2 border border-[.5px] border-black-4 w-full hover:border-primary hover:bg-primary-5 cursor-pointer min-h-[196px] transition-all"
      onClick={() => {
        setCurrentSafeId(safe._id);
        setCurrentParentSafeId(safe.parrentId || safe._id);
        history.push(`/vault/${subaccountSafe?.address}/dashboard`);
      }}
    >
      <div className="flex w-full gap-12">
        <div className="flex w-44 h-44 rounded-[50%] bg-white-10 items-center justify-center">
          <SafeAvatar size="40px" address={safe._id} />
        </div>
        <div className={classes.safeItemHeader}>
          <div className="flex gap-4 items-center">
            <span className={classes.safeItemHeaderTitle}>
              <ShortText text={safe.name} limit={17} />
            </span>
            <img src={VerificationIcon} alt="" />
          </div>
          <div className="flex gap-8 items-center">
            <p className="text-r14 text-regular">{shortenAddress(safe.address)}</p>
            <CopyAddress address={safe.address} />
          </div>
        </div>
      </div>
      {subaccounts.length === 0 && <div className="w-full h-[.5px] bg-black-4 mx-[-24px]" />}
      {subaccounts.length > 0 && (
        <SubAccountsDropdown
          items={[...subaccounts, safe].map((account) => account._id)}
          addresses={[...subaccounts, safe].map((account) => account.address)}
          names={[...subaccounts, safe].map((account) =>
            !account.parrentId ? 'Main Account' : account.name
          )}
          onChange={(val) => {
            if (!isPro) {
              return;
            }
            selectSubAccount(safe._id, val);
          }}
          item={currentSubAccount}
          id={'subAccount' + safe._id}
          actionBottom={
            !isPro && (
              <div className="flex flex-col items-center gap-8 px-16 my-8">
                <div className="flex items-center gap-4">
                  <span className="text-m13 text-regular">Asigna</span>
                  <div className="text-center font-bold text-[13px] leading-[20px] text-black-0 w-[43px] h-24 rounded-[12px] bg-regular flex items-center justify-center">
                    Pro
                  </div>
                </div>
                <span className="text-center text-m13 mb-4">
                  Activate Asigna Pro to enable switching between sub-accounts.
                </span>
                <ButtonV2
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentSafeId(safe._id);
                    setCurrentParentSafeId(safe.parrentId || safe._id);
                    history.push(`/vault/${safe.address}/pro`);
                  }}
                >
                  Activate Subscription
                </ButtonV2>
              </div>
            )
          }
        />
      )}
      <div className="flex gap-16 w-full justify-around">
        <div className={classes.safeItemOwners}>
          <div className={classes.safeItemOwnersWrapper}>
            {safe.owners.map((owner, i) => (
              <div
                className={cx(classes.ownerAvatar, 'border border-black-4 rounded-[12px]')}
                key={owner.address}
              >
                <OwnerAvatar address={owner.address} size="22px" index={index * 10 + i} />
              </div>
            ))}
          </div>
          <div className="flex gap-[2px] text-r13 leading-[20px]">
            <p>{safe.owners.length}</p>
            <p className="text-regular">{safe.owners.length === 1 ? 'Owner' : 'Owners'}</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-[2px] text-r13 leading-[20px]">
          <p>{formatSafeDate(safe.create_time)}</p>
          <p className="text-regular">Creation date</p>
        </div>
      </div>
    </button>
  );
};
